import { useState, useEffect, useCallback } from 'react';
import {
  Group,
  Text,
  Badge,
  ActionIcon,
  Menu,
  Button,
  Stack,
  ThemeIcon,
  Center,
  Loader,
  Tooltip,
  Collapse,
  Box,
} from '@mantine/core';
import {
  Calendar,
  Users,
  Layers,
  Monitor,
  Bell,
  MapPin,
  MoreVertical,
  Plus,
  Clock,
  Send,
  Eye,
  CheckSquare,
  Edit3,
  ChevronDown,
  ChevronRight,
} from 'react-feather';
import { formatDateTime } from '../../../../utils/formatters';
import {
  cohortEventsApi,
  type CohortEventWithDetails,
  type CohortEventStatus,
} from '../../../../services/api/cohortEvents';
import {
  isCohortEventPast,
  showsAttendancePendingBadge,
  summarizeCohortEvents,
} from './cohortEventSummary';

interface CohortEventsSectionProps {
  cohortId: string;
  refreshTrigger?: number;
  onAddHuddle?: () => void;
  onScheduleEvent?: (event: CohortEventWithDetails) => void;
  onEditDateTime?: (event: CohortEventWithDetails) => void;
  onSendInvites?: (event: CohortEventWithDetails) => void;
  onViewRsvps?: (event: CohortEventWithDetails) => void;
  onMarkAttendance?: (event: CohortEventWithDetails) => void;
  onResendInvites?: (event: CohortEventWithDetails) => void;
  /** Opens the event detail drawer when an event row is clicked */
  onEventClick?: (event: CohortEventWithDetails) => void;
  /**
   * MED-1219: when true, the cohort's group-coaching modality is peer-led, so
   * coach-only affordances are suppressed — the "Add Huddle" button is hidden
   * (AC1) and Coach Immersion / Coach Check-In (`coach_huddle`) rows are
   * filtered out (AC2/AC3). Participant Orientation and Cohort Wrap-Up remain
   * (AC4). Driven by the saved coaching sub-type alone via
   * `isPeerLedGroupCoaching`, independent of the Live Session Delivery flag (AC6).
   */
  isPeerLed?: boolean;
}

/**
 * MED-1219: cohort event types that are coach-only concepts and therefore hidden
 * on a peer-led group-coaching cohort (no coach exists to run them).
 */
const COACH_ONLY_EVENT_TYPES = new Set(['coach_immersion', 'coach_huddle']);

// Icon & color map for event types — distinct per type per design spec
const eventIcons: Record<string, typeof Users> = {
  coach_immersion: Users,
  participant_orientation: Layers,
  coach_huddle: Monitor,
  cohort_wrap_up: Bell,
  in_person_booster: MapPin,
};

/** Left border color per event type for visual distinction from group sessions (AC12) */
const eventTypeColors: Record<string, string> = {
  coach_immersion: '#4A7C59',
  participant_orientation: '#9C36B5',
  coach_huddle: '#1098AD',
  cohort_wrap_up: '#E9A23B',
  in_person_booster: '#2D4739',
};

// Status badge configuration
const statusConfig: Record<CohortEventStatus, { label: string; color: string; variant: string }> = {
  not_scheduled: { label: 'Not Scheduled', color: 'gray', variant: 'light' },
  ready: { label: 'Ready', color: 'blue', variant: 'light' },
  scheduled: { label: 'Scheduled', color: 'blue', variant: 'light' },
  awaiting_attendance: { label: 'Awaiting Attendance', color: 'yellow', variant: 'filled' },
  completed: { label: 'Completed', color: 'green', variant: 'light' },
};

/**
 * Displays cohort events (Coach Immersion, Participant Orientation, Huddles, Wrap-Up)
 * Fetches from cohort_events API table with RSVP summaries and status tracking.
 */
export function CohortEventsSection({
  cohortId,
  refreshTrigger,
  onAddHuddle,
  onScheduleEvent,
  onEditDateTime,
  onSendInvites,
  onViewRsvps,
  onMarkAttendance,
  onResendInvites,
  onEventClick,
  isPeerLed = false,
}: CohortEventsSectionProps) {
  const [events, setEvents] = useState<CohortEventWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [isExpanded, setIsExpanded] = useState(true);

  const loadEvents = useCallback(async () => {
    try {
      const data = await cohortEventsApi.list(cohortId);
      setEvents(data);
    } catch {
      // Silently handle - empty state will show
    } finally {
      setLoading(false);
    }
  }, [cohortId]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents, refreshTrigger]);

  // MED-1219: on a peer-led cohort, hide coach-only event rows (Coach Immersion,
  // Coach Check-In). Counts derive from the visible list so the header summary
  // stays consistent with what is actually shown.
  const visibleEvents = isPeerLed
    ? events.filter((e) => !COACH_ONLY_EVENT_TYPES.has(e.event_type))
    : events;

  // MED-1349: an ended event with no attendance counts as awaiting
  // attendance, not as upcoming.
  const summary = summarizeCohortEvents(visibleEvents, new Date());

  return (
    <Box
      style={{
        border: '1px solid #E5E2DC',
        borderRadius: 8,
        overflow: 'hidden',
      }}
    >
      {/* Section Header — accordion pattern */}
      <Group
        justify="space-between"
        style={{
          backgroundColor: 'var(--medley-surface-warm, #F5F4F0)',
          padding: '16px 20px',
          cursor: 'pointer',
          borderBottom: isExpanded ? '1px solid #E5E2DC' : 'none',
        }}
        onClick={() => setIsExpanded((prev) => !prev)}
        wrap="nowrap"
      >
        <Group gap="sm">
          <ThemeIcon color="gray" size={40} radius="md" variant="light">
            <Calendar size={20} />
          </ThemeIcon>
          <div>
            <Text fw={600} size="16px">
              Cohort Events
            </Text>
            <Text size="13px" c="dimmed" mt={4}>
              {summary.completed} completed{' '}<span style={{ fontSize: 18, lineHeight: 1, verticalAlign: 'middle', padding: '0 2px' }}>·</span>{' '}
              {summary.awaiting > 0 && (
                <>
                  {summary.awaiting} awaiting attendance{' '}<span style={{ fontSize: 18, lineHeight: 1, verticalAlign: 'middle', padding: '0 2px' }}>·</span>{' '}
                </>
              )}
              {summary.upcoming} upcoming
            </Text>
          </div>
        </Group>
        <Group gap="sm" onClick={(e) => e.stopPropagation()}>
          {/* MED-1219 AC1: Add Huddle creates a Coach Check-In — hidden on peer-led cohorts */}
          {!isPeerLed && (
            <Button variant="default" size="xs" leftSection={<Plus size={12} />} onClick={onAddHuddle}>
              Add Huddle
            </Button>
          )}
          <ActionIcon
            variant="subtle"
            color="gray"
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded((prev) => !prev);
            }}
          >
            {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </ActionIcon>
        </Group>
      </Group>

      {/* Events List */}
      <Collapse in={isExpanded}>
        <Box style={{ backgroundColor: '#FFFFFF' }}>
        {loading ? (
          <Center py="md">
            <Loader size="sm" />
          </Center>
        ) : (
          <Stack gap={0}>
            {visibleEvents.map((event) => {
              const Icon = eventIcons[event.event_type] || Calendar;
              const status = statusConfig[event.status] || statusConfig.not_scheduled;
              const hasInvitesSent = !!event.invites_sent_at;
              const rsvp = event.rsvp_summary;

              return (
                <Group
                  key={event.id}
                  justify="space-between"
                  style={(theme) => ({
                    padding: '14px 20px',
                    borderBottom: `1px solid ${theme.colors.gray[2]}`,
                    borderLeft: `3px solid ${eventTypeColors[event.event_type] || '#888'}`,
                    cursor: onEventClick ? 'pointer' : undefined,
                  })}
                  onClick={() => onEventClick?.(event)}
                >
                  {/* Left: Icon + Name */}
                  <Group gap="sm" style={{ flex: 1, minWidth: 0 }}>
                    <ThemeIcon size="md" variant="light" color="gray" radius="xl">
                      <Icon size={16} />
                    </ThemeIcon>
                    <div>
                      <Text fw={500} size="sm">
                        {event.event_name}
                      </Text>
                      {event.scheduled_start ? (
                        <Text size="xs" c="dimmed">
                          {formatDateTime(event.scheduled_start)}
                        </Text>
                      ) : (
                        <Text size="xs" c="dimmed">
                          No date set
                        </Text>
                      )}
                    </div>
                  </Group>

                  {/* Center: RSVP summary (if invites sent) */}
                  {hasInvitesSent && rsvp && rsvp.total > 0 && (
                    <Tooltip
                      label={`${rsvp.accepted} accepted, ${rsvp.declined} declined, ${rsvp.tentative} tentative, ${rsvp.pending} pending`}
                    >
                      <Badge variant="light" color="gray" size="sm" style={{ cursor: 'default' }}>
                        {rsvp.accepted} / {rsvp.total} RSVP
                      </Badge>
                    </Tooltip>
                  )}

                  {/* Right: Status + Actions */}
                  <Group gap="sm" onClick={(e) => e.stopPropagation()}>
                    <Badge
                      size="sm"
                      color={status.color}
                      variant={status.variant as 'light' | 'outline' | 'filled'}
                      styles={{
                        root: { textTransform: 'uppercase', letterSpacing: '0.3px' },
                      }}
                    >
                      {status.label}
                    </Badge>

                    {/* AC14: Attendance pending indicator. MED-1349: not when the
                        status badge already says Awaiting Attendance. */}
                    {showsAttendancePendingBadge(event, new Date()) && (
                      <Tooltip label="Event ended - attendance not yet recorded">
                        <Badge size="sm" color="yellow" variant="light">
                          Attendance Pending
                        </Badge>
                      </Tooltip>
                    )}

                    {/* Send Invites button when event is READY */}
                    {event.status === 'ready' && (
                      <Button
                        variant="filled"
                        size="xs"
                        leftSection={<Send size={12} />}
                        onClick={() => onSendInvites?.(event)}
                      >
                        Send Invites
                      </Button>
                    )}

                    {/* Three-dot menu */}
                    <Menu position="bottom-end" withinPortal>
                      <Menu.Target>
                        <ActionIcon variant="subtle" color="gray" size={32}>
                          <MoreVertical size={16} />
                        </ActionIcon>
                      </Menu.Target>
                      <Menu.Dropdown>
                        {/* Set/Edit Date/Time - always available */}
                        <Menu.Item
                          leftSection={<Clock size={14} />}
                          onClick={() =>
                            event.scheduled_start
                              ? onEditDateTime?.(event)
                              : onScheduleEvent?.(event)
                          }
                        >
                          {event.scheduled_start ? 'Edit Date/Time' : 'Set Date/Time'}
                        </Menu.Item>

                        {/* Send Invites - when ready but not yet sent */}
                        {event.status === 'ready' && (
                          <Menu.Item
                            leftSection={<Send size={14} />}
                            onClick={() => onSendInvites?.(event)}
                          >
                            Send Invites
                          </Menu.Item>
                        )}

                        {/* Resend Invites - when invites already sent */}
                        {hasInvitesSent && (
                          <Menu.Item
                            leftSection={<Send size={14} />}
                            onClick={() => onResendInvites?.(event)}
                          >
                            Resend Invites
                          </Menu.Item>
                        )}

                        {/* View RSVPs - when invites sent */}
                        {hasInvitesSent && (
                          <Menu.Item
                            leftSection={<Eye size={14} />}
                            onClick={() => onViewRsvps?.(event)}
                          >
                            View RSVPs
                          </Menu.Item>
                        )}

                        {/* Mark Attendance - when event is past and not yet marked */}
                        {isCohortEventPast(event, new Date()) && !event.attendance_marked && (
                          <Menu.Item
                            leftSection={<CheckSquare size={14} />}
                            onClick={() => onMarkAttendance?.(event)}
                          >
                            Mark Attendance
                          </Menu.Item>
                        )}

                        {/* MED-1349: Edit Attendance - when already marked */}
                        {event.attendance_marked && (
                          <Menu.Item
                            leftSection={<Edit3 size={14} />}
                            onClick={() => onMarkAttendance?.(event)}
                          >
                            Edit Attendance
                          </Menu.Item>
                        )}
                      </Menu.Dropdown>
                    </Menu>
                  </Group>
                </Group>
              );
            })}

            {visibleEvents.length === 0 && !loading && (
              <Text size="sm" c="dimmed" ta="center" py="md">
                No cohort events found
              </Text>
            )}
          </Stack>
        )}
        </Box>
      </Collapse>
    </Box>
  );
}

export default CohortEventsSection;
